// Client for the backend text-to-speech endpoint (Microsoft Edge neural TTS),
// and the queue that plays its MP3s through the shared AnalyserNode so the
// avatar's mouth follows along — see AvatarSpeechContext.
import { getTtsUrl } from "../agentConnector/Api";
import { MAX_TTS_CHARS } from "./speechChunks";

/**
 * Fetch one prepared speech chunk. The queue fixes voice/language for the
 * whole reply; never re-detect language or re-parse a fragment of its markup.
 */
export const fetchTtsChunk = async (text, voice, botId, language, signal) => {
  const spoken = text.trim();
  if (!spoken) throw new Error("Nothing to speak");
  if (spoken.length > MAX_TTS_CHARS) throw new Error("Speech chunk is too long");

  // A form POST keeps long Chinese text out of the URL and stays a simple
  // cross-origin request. bot_id remains in the query for the CORS check.
  const res = await fetch(
    `${getTtsUrl()}?bot_id=${encodeURIComponent(botId || "")}`,
    {
      method: "POST",
      body: new URLSearchParams({ text: spoken, voice, lang: language }),
      signal,
    },
  );
  if (!res.ok) throw new Error("Speech is unavailable. Retry to continue.");
  return res.arrayBuffer();
};

// One prepared chunk ahead keeps startup quick and memory bounded.
export const checkSpeechAbort = (signal) => {
  if (signal.aborted) throw new DOMException("Speech stopped", "AbortError");
};

export async function playSpeechChunks({
  chunks, startIndex = 0, signal, loadAudio, playAudio,
  onWaiting, onPlaying, onProgress,
}) {
  // Handle prefetch rejection immediately, even while the current clip plays.
  const prepare = (index) =>
    Promise.resolve()
      .then(() => {
        checkSpeechAbort(signal);
        return loadAudio(chunks[index], signal);
      })
      .then((audio) => ({ audio }), (error) => ({ error }));

  let pending = prepare(startIndex);
  for (let index = startIndex; index < chunks.length; index += 1) {
    checkSpeechAbort(signal);
    onWaiting();
    const result = await pending;
    checkSpeechAbort(signal);
    if (result.error) throw result.error;
    pending = index + 1 < chunks.length ? prepare(index + 1) : null;
    onPlaying();
    await playAudio(result.audio, signal);
    checkSpeechAbort(signal);
    // Only completed clips advance the retry position.
    onProgress(index + 1);
  }
}

export function playAudioBuffer(context, analyser, audio, signal) {
  return new Promise((resolve, reject) => {
    checkSpeechAbort(signal);
    const source = context.createBufferSource();
    source.buffer = audio;
    source.connect(analyser);
    source.connect(context.destination);

    const cleanup = () => {
      source.onended = null;
      signal.removeEventListener("abort", abort);
      source.disconnect();
    };
    const abort = () => {
      cleanup();
      try { source.stop(); } catch { /* already stopped */ }
      reject(new DOMException("Speech stopped", "AbortError"));
    };
    source.onended = () => {
      cleanup();
      resolve();
    };
    signal.addEventListener("abort", abort, { once: true });
    try {
      source.start();
    } catch (error) {
      cleanup();
      reject(error);
    }
  });
}

// A legitimate resume can finish after a very fast TTS response. Wait briefly,
// but do not leave blocked autoplay pending forever, and stop waiting on cancel.
export function waitForAudioResume(resuming, signal) {
  checkSpeechAbort(signal);
  return new Promise((resolve, reject) => {
    const finish = (error) => {
      clearTimeout(timer);
      signal.removeEventListener("abort", abort);
      if (error) reject(error);
      else resolve();
    };
    const abort = () => finish(new DOMException("Speech stopped", "AbortError"));
    const timer = setTimeout(
      () => finish(new Error("Audio is blocked. Tap Retry to enable playback.")),
      2000,
    );
    signal.addEventListener("abort", abort, { once: true });
    resuming.then(({ error }) => finish(error));
  });
}
